/**
 * The screen rail: the ordered list of screens, and the four things you can do to one.
 *
 * Every mutation goes through the editor store (B9). The store's moves are already safe no-ops at
 * the edges, so disabling the edge buttons here is purely an affordance: pressing a button that
 * does nothing is worse than a button that says it cannot.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { Screen } from './interfaces'
import type { ScreenLayout } from './types'

import { withCount } from '@shared/lib/format'

import { useAssessmentEditorStore } from './store'

/** How much of the first text block the rail shows — enough to tell two screens apart, not enough
 *  to wrap the rail. */
const SUMMARY_LENGTH = 40

interface ScreenListItem {
  id: string
  /** 1-based, because that is how an author counts screens. */
  position: number
  label: string
  summary: string
  /** The card's second line: the activity count, and `submits` for the screen that ends the
   *  attempt. */
  metaLabel: string
  /** What the card's layout icon shows. An absent `layout` DISPLAYS as `single`, exactly as the
   *  screen form does — the document is not written until the author picks a value (B9). */
  layout: ScreenLayout
  layoutLabel: string
  isSelected: boolean
  isFirstInOrder: boolean
  isLastInOrder: boolean
  /** The schema's `isLast` — the screen whose CTA submits the attempt. Not the rail's edge. */
  isSubmitScreen: boolean
}

interface ScreenListReturn {
  refScreens: ComputedRef<ScreenListItem[]>
  refHasScreens: ComputedRef<boolean>
  selectScreen: (screenId: string) => void
  addScreen: () => void
  removeScreen: (screenId: string) => void
  moveScreenUp: (screenId: string) => void
  moveScreenDown: (screenId: string) => void
}

/**
 * A `Screen` has no `title` in the schema, and inventing one would be a document change this task
 * may not make. So the rail labels a screen by its position (stable, always present) and describes
 * it with its first text block and activity count — the two things already in the document that
 * tell one screen from another.
 */
const LAYOUT_LABELS: Record<ScreenLayout, string> = {
  'single': 'Single column',
  'two-column': 'Two columns',
}

const summarise = (screen: Screen): string => {
  const text = screen.content.find(block => block.type === 'text')
  const value = text?.type === 'text' ? text.value.trim() : ''
  if (!value) {
    return screen.content.length ? 'No text yet' : 'Empty screen'
  }
  return value.length > SUMMARY_LENGTH ? `${value.slice(0, SUMMARY_LENGTH)}…` : value
}

const metaLabel = (screen: Screen): string =>
  [
    withCount(screen.activities.length, { one: 'activity', other: 'activities' }),
    screen.isLast === true ? 'submits' : '',
  ].filter(Boolean).join(' · ')

export const useScreenList = (): ScreenListReturn => {
  const store = useAssessmentEditorStore()

  const refScreens = computed<ScreenListItem[]>(() => {
    const screens = store.getScreens
    return screens.map((screen, index) => ({
      id: screen.id,
      position: index + 1,
      label: `Screen ${index + 1}`,
      summary: summarise(screen),
      metaLabel: metaLabel(screen),
      layout: screen.layout ?? 'single',
      layoutLabel: LAYOUT_LABELS[screen.layout ?? 'single'],
      isSelected: screen.id === store.getSelectedScreenId,
      isFirstInOrder: index === 0,
      isLastInOrder: index === screens.length - 1,
      isSubmitScreen: screen.isLast === true,
    }))
  })

  const refHasScreens = computed<boolean>(() => store.hasScreens)

  const selectScreen = (screenId: string): void => {
    store.selectScreen(screenId)
  }

  /** The store selects the new screen itself and returns its id. */
  const addScreen = (): void => {
    store.addScreen()
  }

  const removeScreen = (screenId: string): void => {
    store.removeScreen(screenId)
  }

  const moveScreenUp = (screenId: string): void => {
    store.moveScreenUp(screenId)
  }

  const moveScreenDown = (screenId: string): void => {
    store.moveScreenDown(screenId)
  }

  return {
    refScreens,
    refHasScreens,
    selectScreen,
    addScreen,
    removeScreen,
    moveScreenUp,
    moveScreenDown,
  }
}
